/**
 * One writing session, outliving any canvas teardown.
 *
 * Manuscript Studio now keeps one `WritingPad` and one engine alive. This object
 * still owns the durable page across the moments a canvas genuinely must end:
 * reloading the app, opening another document, or unmounting the feature. The
 * pad hands the page here before teardown and takes it back on the next mount.
 *
 * The session also refuses to be held by two engines at once. Two live engines
 * on one page would each think they owned the undo history, and the last one to
 * write would win — which is the same class of bug wearing a different hat.
 */

import { emptyInkPage, type InkEngine, type InkPage } from './InkEngine';
import type { InkTool } from './types';
import type { OverrideMap } from './overrides';
import type { StaffLayout } from '../theory/staff';
import { fromRelativeAll, toRelativeAll } from '../storage/staffRelative';

/** Settings that belong to the learner rather than to the page. */
export interface InkPreferences {
  tool: InkTool;
  color: string;
  width: number;
  penOnly: boolean;
}

export interface InkSession {
  /** The page, whenever no engine holds it. */
  page: InkPage;
  /** Manual accidental corrections, which are keyed to the strokes they belong to. */
  overrides: OverrideMap;
  preferences: InkPreferences;
  /** The engine currently holding the page, if any. */
  readonly holder: InkEngine | null;
  /** How many engines have ever attached. Used by the lifecycle harness. */
  readonly attachCount: number;

  /**
   * Give the page to an engine. Returns a detach function, so a React effect can
   * hand it straight back as its cleanup.
   */
  attach: (engine: InkEngine) => () => void;
  /**
   * Tell the session where the staff is now. Full screen and compact mode have
   * different pixel grids; the page is moved through staff-relative units so a
   * note stays on the same line instead of keeping an obsolete canvas y value.
   */
  reflow: (engine: InkEngine, layout: StaffLayout) => void;
}

export function sameStaffLayout(a: StaffLayout, b: StaffLayout): boolean {
  return (
    a.lineGap === b.lineGap &&
    a.bottomLineY === b.bottomLineY &&
    a.writingX === b.writingX &&
    a.startX === b.startX &&
    a.endX === b.endX
  );
}

function moveStrokes(strokes: InkPage['strokes'], from: StaffLayout, to: StaffLayout) {
  return fromRelativeAll(toRelativeAll(strokes, from), to);
}

export function reflowInkPage(page: InkPage, from: StaffLayout, to: StaffLayout): InkPage {
  return {
    ...page,
    strokes: moveStrokes(page.strokes, from, to),
    past: page.past.map((snapshot) => moveStrokes(snapshot, from, to)),
    future: page.future.map((snapshot) => moveStrokes(snapshot, from, to)),
  };
}

export function createInkSession(
  initial?: Partial<Pick<InkSession, 'page' | 'overrides' | 'preferences'>>,
): InkSession {
  let holder: InkEngine | null = null;
  let attachCount = 0;
  let lastLayout: StaffLayout | null = null;

  const session: InkSession = {
    page: initial?.page ?? emptyInkPage(),
    overrides: initial?.overrides ?? {},
    preferences: initial?.preferences ?? {
      tool: 'pen',
      color: '#12161d',
      width: 7.5,
      penOnly: false,
    },
    get holder() {
      return holder;
    },
    get attachCount() {
      return attachCount;
    },
    attach(engine: InkEngine) {
      if (holder && holder !== engine) {
        // Not a warning: two engines writing one page corrupts the history in a
        // way that shows up much later as "undo did something strange". React
        // runs every cleanup before every setup in a commit, so a correct
        // swap never reaches this.
        throw new Error('This ink session is already held by another engine');
      }
      holder = engine;
      attachCount += 1;
      engine.hydrate(session.page);
      return () => {
        if (holder !== engine) return; // already handed on; nothing to take back
        // Read the page back *before* the engine is torn down, or the detach
        // ordering silently decides whether the last stroke survives.
        session.page = engine.serialize();
        if (engine.layout) lastLayout = engine.layout;
        holder = null;
      };
    },
    reflow(engine: InkEngine, layout: StaffLayout) {
      if (holder !== engine) return;
      const previous = lastLayout ?? engine.layout;
      if (previous && !sameStaffLayout(previous, layout)) {
        const moved = reflowInkPage(engine.serialize(), previous, layout);
        engine.hydrate(moved);
        session.page = moved;
      }
      engine.layout = layout;
      lastLayout = layout;
    },
  };

  return session;
}
